import { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { rolePermissionsApi, customRolesApi, accessDenialsApi, grantHistoryApi } from '../lib/api';
import { NAV_CATALOG, MANAGEABLE_ROLES, isDefaultVisible } from '../config/navCatalog';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { toast } from 'sonner';
import { FloppyDisk, ArrowCounterClockwise, Plus, Trash, PencilSimple, ShieldWarning, Check, DownloadSimple, ClockCounterClockwise, EnvelopeSimple } from '@phosphor-icons/react';

export default function RolePermissions() {
  const { language, t } = useLanguage();
  const [overrides, setOverrides] = useState({});   // { role: Set(paths) }
  const [dirty, setDirty] = useState({});           // { role: true }
  const [customRoles, setCustomRoles] = useState([]); // [{code,label_fr,label_en,allowed_paths}]
  const [denials, setDenials] = useState([]);
  const [retentionDays, setRetentionDays] = useState(90);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newLabelFr, setNewLabelFr] = useState('');
  const [newLabelEn, setNewLabelEn] = useState('');
  const [creating, setCreating] = useState(false);

  const isCustom = (r) => customRoles.some(c => c.code === r);
  const roleLabel = (r) => {
    const c = customRoles.find(x => x.code === r);
    if (c) return language === 'fr' ? c.label_fr : (c.label_en || c.label_fr);
    return t(r) || r;
  };

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [permsRes, customRes, denRes] = await Promise.all([
        rolePermissionsApi.getAll(),
        customRolesApi.list(),
        accessDenialsApi.list().catch(() => ({ data: { denials: [] } })),
      ]);
      const raw = permsRes.data.overrides || {};
      const custom = customRes.data?.custom || [];
      setDenials(denRes.data?.denials || []);
      accessDenialsApi.retention().then(({ data }) => setRetentionDays(data?.days || 90)).catch(() => {});
      grantHistoryApi.list().then(({ data }) => setHistory(data?.history || [])).catch(() => {});
      const next = {};
      MANAGEABLE_ROLES.forEach(role => {
        if (Array.isArray(raw[role])) {
          next[role] = new Set(raw[role]);
        } else {
          next[role] = new Set(NAV_CATALOG.filter(p => isDefaultVisible(p, role)).map(p => p.path));
        }
      });
      custom.forEach(c => { next[c.code] = new Set(c.allowed_paths || []); });
      setCustomRoles(custom);
      setOverrides(next);
      setDirty({});
    } catch (e) {
      toast.error(t('error') || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (role, path) => {
    setOverrides(prev => {
      const set = new Set(prev[role]);
      if (set.has(path)) set.delete(path); else set.add(path);
      return { ...prev, [role]: set };
    });
    setDirty(prev => ({ ...prev, [role]: true }));
  };

  const saveRole = async (role) => {
    setSaving(role);
    try {
      const paths = Array.from(overrides[role] || []);
      if (isCustom(role)) {
        await customRolesApi.update(role, { allowed_paths: paths });
      } else {
        await rolePermissionsApi.set(role, paths);
      }
      toast.success(language === 'fr' ? `Droits de « ${roleLabel(role)} » enregistrés` : 'Saved');
      setDirty(prev => ({ ...prev, [role]: false }));
    } catch (e) {
      toast.error(e.response?.data?.detail || (t('error') || 'Erreur'));
    } finally {
      setSaving(null);
    }
  };

  const resetRole = async (role) => {
    if (!window.confirm(language === 'fr'
      ? `Réinitialiser « ${roleLabel(role)} » aux droits par défaut ?`
      : `Reset "${roleLabel(role)}" to defaults?`)) return;
    try {
      await rolePermissionsApi.reset(role);
      toast.success(language === 'fr' ? 'Réinitialisé' : 'Reset');
      load();
    } catch (e) {
      toast.error(t('error') || 'Erreur');
    }
  };

  const createProfile = async () => {
    if (!newLabelFr.trim()) return;
    setCreating(true);
    try {
      await customRolesApi.create({ label_fr: newLabelFr.trim(), label_en: newLabelEn.trim() || undefined });
      toast.success(language === 'fr' ? 'Profil créé' : 'Profile created');
      setCreateOpen(false);
      setNewLabelFr(''); setNewLabelEn('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (t('error') || 'Erreur'));
    } finally {
      setCreating(false);
    }
  };

  const renameProfile = async (role) => {
    const current = customRoles.find(c => c.code === role);
    const label = window.prompt(language === 'fr' ? 'Nouveau nom du profil' : 'New profile name', current?.label_fr || '');
    if (!label || !label.trim()) return;
    try {
      await customRolesApi.update(role, { label_fr: label.trim() });
      toast.success(language === 'fr' ? 'Profil renommé' : 'Renamed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (t('error') || 'Erreur'));
    }
  };

  const deleteProfile = async (role) => {
    if (!window.confirm(language === 'fr'
      ? `Supprimer le profil « ${roleLabel(role)} » ? Les utilisateurs liés perdront leurs accès.`
      : `Delete profile "${roleLabel(role)}"?`)) return;
    try {
      await customRolesApi.remove(role);
      toast.success(language === 'fr' ? 'Profil supprimé' : 'Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (t('error') || 'Erreur'));
    }
  };

  const customCodes = useMemo(() => customRoles.map(c => c.code), [customRoles]);
  const roles = useMemo(() => [...MANAGEABLE_ROLES, ...customCodes], [customCodes]);

  const pageLabel = (path) => {
    const p = NAV_CATALOG.find(x => x.path === path);
    if (!p) return path;
    return language === 'fr' ? p.labelFr : p.labelEn;
  };

  const grantFromDenial = async (den) => {
    const set = new Set(overrides[den.role]);
    if (set.has(den.page)) {
      // already granted in UI but not saved — just dismiss
    } else {
      set.add(den.page);
    }
    try {
      const paths = Array.from(set);
      if (den.is_custom_role) {
        await customRolesApi.update(den.role, { allowed_paths: paths });
      } else {
        await rolePermissionsApi.set(den.role, paths);
      }
      await accessDenialsApi.dismiss(den.id);
      toast.success(language === 'fr'
        ? `« ${pageLabel(den.page)} » accordée à ${roleLabel(den.role)}`
        : `Granted "${pageLabel(den.page)}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (t('error') || 'Erreur'));
    }
  };

  const dismissDenial = async (den) => {
    try {
      await accessDenialsApi.dismiss(den.id);
      setDenials(prev => prev.filter(d => d.id !== den.id));
    } catch (e) {
      toast.error(t('error') || 'Erreur');
    }
  };

  const changeRetention = async (days) => {
    const prev = retentionDays;
    setRetentionDays(days);
    try {
      const { data } = await accessDenialsApi.setRetention(Number(days));
      toast.success(language === 'fr'
        ? `Rétention : ${days} jours${data.purged ? ` — ${data.purged} entrée(s) purgée(s)` : ''}`
        : `Retention: ${days} days`);
      if (data.purged) load();
    } catch (e) {
      setRetentionDays(prev);
      toast.error(e.response?.data?.detail || (t('error') || 'Erreur'));
    }
  };

  const sendSummaryNow = async () => {
    try {
      const { data } = await accessDenialsApi.sendSummary();
      toast.success(language === 'fr'
        ? (data.sent ? `Résumé envoyé à ${data.sent} admin(s)` : 'Aucun refus ou aucun admin à notifier')
        : (data.sent ? `Summary sent to ${data.sent} admin(s)` : 'Nothing to send'));
    } catch (e) {
      toast.error(e.response?.data?.detail || (t('error') || 'Erreur'));
    }
  };

  const exportDenials = async () => {
    try {
      const { data } = await accessDenialsApi.export();
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `journal_acces_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('error') || 'Erreur');
    }
  };

  const clearDenials = async () => {
    if (!window.confirm(language === 'fr' ? 'Vider le journal d\'accès ?' : 'Clear access log?')) return;
    try {
      await accessDenialsApi.clear();
      setDenials([]);
      toast.success(language === 'fr' ? 'Journal vidé' : 'Cleared');
    } catch (e) {
      toast.error(t('error') || 'Erreur');
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US'); }
    catch { return iso; }
  };

  if (loading) {
    return <div className="p-8 text-center text-[#64748B]">Chargement…</div>;
  }

  return (
    <div className="space-y-6" data-testid="role-permissions-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">
            {language === 'fr' ? 'Droits par profil' : 'Role permissions'}
          </h1>
          <p className="font-manrope text-sm text-[#64748B] mt-1 max-w-2xl">
            {language === 'fr'
              ? 'Cochez les pages visibles pour chaque profil. Les accès sont appliqués côté menu ET côté API. L\'Administrateur a toujours accès à tout.'
              : 'Tick the pages each profile can see. Access is enforced on the menu AND the API. Administrator always has full access.'}
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#0052FF] hover:bg-[#0043D1] shrink-0"
          data-testid="create-profile-btn"
        >
          <Plus size={18} className="mr-2" />
          {language === 'fr' ? 'Nouveau profil' : 'New profile'}
        </Button>
      </div>

      {customRoles.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="custom-profiles-list">
          {customRoles.map(c => (
            <span key={c.code} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EFF6FF] text-[#0052FF] text-xs font-medium border border-[#BFDBFE]">
              {language === 'fr' ? c.label_fr : (c.label_en || c.label_fr)}
              <button onClick={() => renameProfile(c.code)} className="hover:text-[#0043D1]" title={language === 'fr' ? 'Renommer' : 'Rename'} data-testid={`rename-profile-${c.code}`}>
                <PencilSimple size={13} />
              </button>
              <button onClick={() => deleteProfile(c.code)} className="hover:text-[#EF4444]" title={language === 'fr' ? 'Supprimer' : 'Delete'} data-testid={`delete-profile-${c.code}`}>
                <Trash size={13} />
              </button>
            </span>
          ))}
        </div>
      )}

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm font-manrope border-collapse">
            <thead>
              <tr className="bg-[#0F172A] text-white">
                <th className="text-left py-3 px-4 font-semibold sticky left-0 bg-[#0F172A] z-10 min-w-[200px]">
                  {language === 'fr' ? 'Page' : 'Page'}
                </th>
                {roles.map(r => (
                  <th key={r} className={`py-3 px-2 font-semibold text-center whitespace-nowrap text-xs ${isCustom(r) ? 'bg-[#1E293B]' : ''}`}>
                    {roleLabel(r)}
                    {isCustom(r) && <span className="block text-[9px] font-normal text-[#93C5FD]">{language === 'fr' ? 'personnalisé' : 'custom'}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {NAV_CATALOG.map((page, idx) => (
                <tr key={page.path} className={idx % 2 ? 'bg-[#F8FAFC]' : 'bg-white'}>
                  <td className="py-2 px-4 sticky left-0 z-10 font-medium text-[#0F172A] border-r border-[#E2E8F0]"
                      style={{ backgroundColor: idx % 2 ? '#F8FAFC' : '#FFFFFF' }}>
                    {language === 'fr' ? page.labelFr : page.labelEn}
                    <span className="block text-[10px] text-[#94A3B8]">{page.path}</span>
                  </td>
                  {roles.map(r => (
                    <td key={r} className="py-2 px-2 text-center">
                      <Checkbox
                        checked={overrides[r]?.has(page.path) || false}
                        onCheckedChange={() => toggle(r, page.path)}
                        data-testid={`perm-${r}-${page.path}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#F1F5F9] border-t border-[#E2E8F0]">
                <td className="py-2 px-4 sticky left-0 bg-[#F1F5F9] z-10 font-semibold text-xs text-[#64748B]">
                  {language === 'fr' ? 'Actions' : 'Actions'}
                </td>
                {roles.map(r => (
                  <td key={r} className="py-2 px-2 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <Button
                        size="sm"
                        onClick={() => saveRole(r)}
                        disabled={!dirty[r] || saving === r}
                        className="h-7 px-2 bg-[#0052FF] hover:bg-[#0043D1] text-[11px]"
                        data-testid={`save-perm-${r}`}
                      >
                        <FloppyDisk size={13} className="mr-1" />
                        {saving === r ? '…' : (language === 'fr' ? 'Enreg.' : 'Save')}
                      </Button>
                      {isCustom(r) ? (
                        <button
                          onClick={() => deleteProfile(r)}
                          className="text-[10px] text-[#64748B] hover:text-[#EF4444] inline-flex items-center gap-0.5"
                          data-testid={`delete-perm-${r}`}
                        >
                          <Trash size={11} /> {language === 'fr' ? 'Suppr.' : 'Delete'}
                        </button>
                      ) : (
                        <button
                          onClick={() => resetRole(r)}
                          className="text-[10px] text-[#64748B] hover:text-[#EF4444] inline-flex items-center gap-0.5"
                          data-testid={`reset-perm-${r}`}
                        >
                          <ArrowCounterClockwise size={11} /> {language === 'fr' ? 'Défaut' : 'Default'}
                        </button>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {/* Journal d'accès — blocked API calls so admins can grant missing pages */}
      <Card className="border border-[#E2E8F0] shadow-none" data-testid="access-denials-panel">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <ShieldWarning size={20} className="text-amber-600" />
              <h2 className="font-outfit text-lg font-semibold text-[#0F172A]">
                {language === 'fr' ? 'Journal d\'accès refusés' : 'Access denial log'}
              </h2>
            </div>
            {denials.length > 0 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={sendSummaryNow}
                  className="text-xs text-[#0052FF] hover:text-[#0043D1] inline-flex items-center gap-1"
                  data-testid="send-summary-btn"
                >
                  <EnvelopeSimple size={13} /> {language === 'fr' ? 'Envoyer le résumé' : 'Email summary'}
                </button>
                <button
                  onClick={exportDenials}
                  className="text-xs text-[#0052FF] hover:text-[#0043D1] inline-flex items-center gap-1"
                  data-testid="export-denials-btn"
                >
                  <DownloadSimple size={13} /> {language === 'fr' ? 'Exporter Excel' : 'Export Excel'}
                </button>
                <button
                  onClick={clearDenials}
                  className="text-xs text-[#64748B] hover:text-[#EF4444] inline-flex items-center gap-1"
                  data-testid="clear-denials-btn"
                >
                  <Trash size={13} /> {language === 'fr' ? 'Vider' : 'Clear'}
                </button>
              </div>
            )}
          </div>
          <p className="font-manrope text-xs text-[#64748B] mb-4">
            {language === 'fr'
              ? 'Appels API bloqués par les droits d\'un profil. Accordez la page manquante en un clic.'
              : 'API calls blocked by a profile\'s rights. Grant the missing page in one click.'}
          </p>

          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="retention-control">
            <span className="text-xs font-manrope text-[#64748B]">
              {language === 'fr' ? 'Conserver les entrées pendant' : 'Keep entries for'}
            </span>
            <Select value={String(retentionDays)} onValueChange={(v) => changeRetention(Number(v))}>
              <SelectTrigger className="h-8 w-[130px] text-xs border-[#E2E8F0]" data-testid="retention-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[7, 30, 90, 180, 365].map(d => (
                  <SelectItem key={d} value={String(d)}>
                    {d} {language === 'fr' ? 'jours' : 'days'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs font-manrope text-[#94A3B8]">
              {language === 'fr' ? '(purge automatique quotidienne)' : '(auto-purged daily)'}
            </span>
          </div>

          {denials.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#94A3B8] font-manrope" data-testid="no-denials">
              {language === 'fr' ? 'Aucun accès refusé récemment.' : 'No recent denials.'}
            </div>
          ) : (
            <div className="space-y-2">
              {denials.map(den => {
                const known = NAV_CATALOG.some(x => x.path === den.page);
                const canGrant = overrides[den.role] !== undefined && known;
                return (
                  <div
                    key={den.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-[#FFFBEB] border border-amber-100"
                    data-testid={`denial-${den.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-manrope text-[#0F172A]">
                        <span className="font-semibold">{roleLabel(den.role)}</span>
                        <span className="text-[#64748B]">
                          {' '}{language === 'fr' ? 'bloqué sur' : 'blocked on'}{' '}
                        </span>
                        <span className="font-semibold text-amber-700">{pageLabel(den.page)}</span>
                        {den.count > 1 && (
                          <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-800">
                            ×{den.count}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#94A3B8] font-mono truncate">
                        {den.method} {den.sample_path} · {fmtTime(den.last_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => grantFromDenial(den)}
                        disabled={!canGrant}
                        className="h-8 text-xs bg-[#0052FF] hover:bg-[#0043D1]"
                        data-testid={`grant-denial-${den.id}`}
                      >
                        <Check size={14} className="mr-1" />
                        {language === 'fr' ? 'Accorder la page' : 'Grant page'}
                      </Button>
                      <button
                        onClick={() => dismissDenial(den)}
                        className="text-xs text-[#64748B] hover:text-[#EF4444]"
                        title={language === 'fr' ? 'Ignorer' : 'Dismiss'}
                        data-testid={`dismiss-denial-${den.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historique des octrois — page grants/revokes per profile */}
      <Card className="border border-[#E2E8F0] shadow-none" data-testid="grant-history-panel">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <ClockCounterClockwise size={20} className="text-[#0052FF]" />
            <h2 className="font-outfit text-lg font-semibold text-[#0F172A]">
              {language === 'fr' ? 'Historique des octrois' : 'Grant history'}
            </h2>
          </div>
          <p className="font-manrope text-xs text-[#64748B] mb-4">
            {language === 'fr'
              ? 'Pages accordées ou retirées à chaque profil, avec l\'auteur et la date.'
              : 'Pages granted or revoked per profile, with who and when.'}
          </p>
          {history.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#94A3B8] font-manrope" data-testid="no-history">
              {language === 'fr' ? 'Aucun changement enregistré.' : 'No changes logged yet.'}
            </div>
          ) : (
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {history.map(h => {
                const grant = h.action === 'grant';
                return (
                  <div key={h.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]" data-testid={`history-${h.id}`}>
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 ${grant ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                      {grant ? <Check size={14} /> : <Trash size={14} />}
                    </span>
                    <div className="min-w-0 text-sm font-manrope text-[#0F172A]">
                      <span className="font-semibold text-amber-700">{pageLabel(h.page)}</span>
                      <span className="text-[#64748B]">
                        {' '}{grant
                          ? (language === 'fr' ? 'accordée à' : 'granted to')
                          : (language === 'fr' ? 'retirée à' : 'revoked from')}{' '}
                      </span>
                      <span className="font-semibold">{roleLabel(h.role)}</span>
                      <span className="block text-[11px] text-[#94A3B8]">
                        {h.by || '—'} · {fmtTime(h.at)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {language === 'fr' ? 'Nouveau profil personnalisé' : 'New custom profile'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Nom du profil' : 'Profile name'}</Label>
              <Input
                value={newLabelFr}
                onChange={(e) => setNewLabelFr(e.target.value)}
                placeholder={language === 'fr' ? 'ex. Caissier Junior' : 'e.g. Junior Cashier'}
                data-testid="new-profile-name-fr"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Nom (anglais, optionnel)' : 'Name (English, optional)'}</Label>
              <Input
                value={newLabelEn}
                onChange={(e) => setNewLabelEn(e.target.value)}
                placeholder="e.g. Junior Cashier"
                data-testid="new-profile-name-en"
              />
            </div>
            <p className="text-xs text-[#64748B]">
              {language === 'fr'
                ? 'Après création, cochez les pages accessibles dans le tableau puis enregistrez.'
                : 'After creating, tick the accessible pages in the table then save.'}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>{t('cancel') || 'Annuler'}</Button>
            <Button
              type="button"
              onClick={createProfile}
              disabled={!newLabelFr.trim() || creating}
              className="bg-[#0052FF] hover:bg-[#0043D1]"
              data-testid="confirm-create-profile-btn"
            >
              {creating ? '…' : (language === 'fr' ? 'Créer' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
